import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormControl, TextField, IconButton, Button, Stack, Box } from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailRecipients = ({ name }) => {
    const { control } = useFormContext();
    const [errors, setErrors] = useState({});

    const validateEmail = (email) => {
        if (!email || email.trim() === '') return false;
        return emailRegex.test(email.trim());
    };

    const isDuplicateEmail = (emails, email, currentIndex) => {
        const trimmedEmail = email.trim().toLowerCase();
        return emails.some((e, index) => index !== currentIndex && e.trim().toLowerCase() === trimmedEmail);
    };

    const updateEmail = (emails, index, value, onChange) => {
        const newEmails = [...emails];
        newEmails[index] = value;
        onChange(newEmails);
    };

    const clearError = (index) => {
        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            return newErrors;
        });
    };

    return (
        <FormControl fullWidth>
            <Controller
                name={name}
                control={control}
                defaultValue={[]}
                render={({ field: { onChange, value } }) => {
                    const emails = value || [];
                    const hasInvalidEmail = emails.some((email, index) => {
                        if (!validateEmail(email)) return true;
                        if (isDuplicateEmail(emails, email, index)) return true;
                        return !!errors[index];
                    });

                    const handleEmailChange = (index, newValue) => {
                        updateEmail(emails, index, newValue, onChange);
                        clearError(index);
                    };

                    const handleEmailBlur = (index) => {
                        const email = emails[index];
                        if (!email || email.trim() === '') {
                            setErrors((prev) => ({ ...prev, [index]: 'Email is required' }));
                        } else if (!validateEmail(email)) {
                            setErrors((prev) => ({ ...prev, [index]: 'Please enter a valid email address' }));
                        } else if (isDuplicateEmail(emails, email, index)) {
                            setErrors((prev) => ({ ...prev, [index]: 'This email already exists' }));
                        } else {
                            updateEmail(emails, index, email.trim(), onChange);
                            clearError(index);
                        }
                    };

                    const handleDeleteEmail = (index) => {
                        const newEmails = emails.filter((_, i) => i !== index);
                        onChange(newEmails);
                        clearError(index);
                    };

                    const handleAddEmail = () => {
                        onChange([...emails, '']);
                    };

                    return (
                        <Stack spacing={2}>
                            {emails.map((email, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Box key={`${name}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TextField
                                        label={`Recipient Email ${index + 1}`} // wrap
                                        value={email}
                                        onChange={(e) => handleEmailChange(index, e.target.value)}
                                        onBlur={() => handleEmailBlur(index)}
                                        error={!!errors[index]}
                                        helperText={errors[index]}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                    <IconButton
                                        color="error" // wrap
                                        onClick={() => handleDeleteEmail(index)}
                                    >
                                        <DeleteOutlined />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button variant="outlined" onClick={handleAddEmail} disabled={hasInvalidEmail} sx={{ fontSize: '14px', fontWeight: 600, textTransform: 'none' }}>
                                Add Email
                            </Button>
                        </Stack>
                    );
                }}
            />
        </FormControl>
    );
};

export default EmailRecipients;
